import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, InputAdornment, Pagination, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import api from "../../api/client";
import { PageHeader, SectionCard, StatCard, EmptyState, useConfirm } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";

function canEditTimesheet(status) {
  return status !== "Approved";
}

function EditTimesheetDialog({ open, timesheet, onClose, onSaved }) {
  const [tasks, setTasks] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && timesheet) {
      setTasks(
        (timesheet.taskDetails || []).map((t) => ({
          _id: t._id,
          taskName: t.taskName || "",
          projectName: t.projectName || t.project || "",
          duration: t.duration != null ? String(t.duration) : "",
        }))
      );
    }
  }, [open, timesheet]);

  const totalHours = useMemo(
    () => tasks.reduce((s, t) => s + (parseFloat(t.duration) || 0), 0),
    [tasks]
  );

  const updateTask = (idx, field, value) => {
    if (field === "duration" && value && !/^\d*\.?\d*$/.test(value)) return;
    setTasks((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  };

  const save = async () => {
    if (!timesheet) return;
    if (tasks.some((t) => !t.taskName.trim() || !t.duration)) {
      toast.error("Fill all task names and hours");
      return;
    }
    if (totalHours <= 0) return toast.error("Total hours must be > 0");
    if (totalHours > 24) return toast.error("Total cannot exceed 24 hours");

    setSaving(true);
    try {
      await Promise.all(
        tasks.map((t) =>
          api.put(`/api/timesheet/${timesheet._id}/tasks/${t._id}`, {
            taskName: t.taskName.trim(),
            projectName: t.projectName.trim(),
            duration: Number(t.duration),
          })
        )
      );
      toast.success("Timesheet updated");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (!timesheet) return null;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700 }}>
        Edit timesheet — {new Date(timesheet.date).toLocaleDateString("en-IN", {
          weekday: "long", day: "numeric", month: "long", year: "numeric",
        })}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {tasks.map((t, i) => (
            <Box key={t._id} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1.5, color: "text.secondary" }}>
                Task {i + 1}
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth size="small" label="Task name *"
                    value={t.taskName}
                    onChange={(e) => updateTask(i, "taskName", e.target.value)}
                  />
                </Grid>
                <Grid item xs={8} md={4}>
                  <TextField
                    fullWidth size="small" label="Project (optional)"
                    value={t.projectName}
                    onChange={(e) => updateTask(i, "projectName", e.target.value)}
                  />
                </Grid>
                <Grid item xs={4} md={2}>
                  <TextField
                    fullWidth size="small" label="Hours *"
                    value={t.duration}
                    onChange={(e) => updateTask(i, "duration", e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.main", textAlign: "right" }}>
            Total: {totalHours.toFixed(1)} hrs
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function TimesheetListNew() {
  const nav = useNavigate();
  const confirm = useConfirm();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState("");
  const [editTarget, setEditTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/timesheet/logineduser?page=${page}&limit=10`);
      setRows(Array.isArray(res.data?.timesheets) ? res.data.timesheets : []);
      setTotal(res.data?.total || 0);
      setTotalPages(res.data?.pages || 1);
    } catch (err) {
      toast.error(extractErrorMessage(err));
      setRows([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [page]);

  const totalHours = useMemo(
    () => rows.reduce((s, t) => s + (t.taskDetails || []).reduce((ss, x) => ss + (parseFloat(x.duration) || 0), 0), 0),
    [rows]
  );

  const totalTasks = rows.reduce((s, t) => s + (t.taskDetails?.length || 0), 0);

  const filtered = rows.filter((t) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (t.taskDetails || []).some((x) => (x.taskName || "").toLowerCase().includes(q));
  });

  const deleteTask = async (timesheetId, taskId) => {
    const ok = await confirm({ title: "Delete task?", message: "This cannot be undone.", confirmText: "Delete", destructive: true });
    if (!ok) return;
    try {
      await api.delete(`/api/timesheet/delete/${timesheetId}/task/${taskId}`);
      toast.success("Task removed");
      load();
    } catch (err) { toast.error(extractErrorMessage(err)); }
  };

  return (
    <Box p={3}>
      <PageHeader
        title="My Timesheets"
        subtitle="Your daily work log"
        icon={<AccessTimeOutlinedIcon />}
        actions={[
          <Button key="new" variant="contained" startIcon={<AddIcon />} onClick={() => nav("/submit-timesheet")}>
            Log Time
          </Button>,
        ]}
      />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 2, mb: 3 }}>
        <StatCard label="Total Entries" value={total} helperText="All time" color="primary" />
        <StatCard label="Tasks This Page" value={totalTasks} color="success" />
        <StatCard label="Hours This Page" value={totalHours.toFixed(1)} helperText="Logged" color="warning" />
      </Box>

      <SectionCard
        title="Entries"
        actions={[
          <TextField
            key="s" size="small" placeholder="Search task…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ width: 220 }}
          />,
        ]}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No timesheets yet"
            description="Start logging your daily work to track your time."
            cta={<Button variant="contained" startIcon={<AddIcon />} onClick={() => nav("/submit-timesheet")}>Log first entry</Button>}
          />
        ) : (
          <Stack spacing={1.5}>
            {filtered.map((t) => {
              const isOpen = !!expanded[t._id];
              const rowTotal = (t.taskDetails || []).reduce((s, x) => s + (parseFloat(x.duration) || 0), 0);
              return (
                <Box key={t._id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, overflow: "hidden" }}>
                  <Stack
                    direction="row" alignItems="center" spacing={{ xs: 1.25, sm: 2 }}
                    onClick={() => setExpanded((e) => ({ ...e, [t._id]: !e[t._id] }))}
                    sx={{ p: { xs: 1.5, sm: 2 }, cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
                  >
                    <Box
                      sx={{
                        minWidth: { xs: 44, sm: 56 }, textAlign: "center", borderRadius: 2,
                        bgcolor: "#F4EDFA", color: "primary.main", py: 1, px: { xs: 1, sm: 1.5 }, flexShrink: 0,
                      }}
                    >
                      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        {new Date(t.date).toLocaleDateString("en-IN", { month: "short" })}
                      </Typography>
                      <Typography sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 800, lineHeight: 1 }}>
                        {new Date(t.date).getDate()}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: 14, fontWeight: 700 }}>
                        {new Date(t.date).toLocaleDateString("en-IN", isMobile
                          ? { weekday: "short", day: "numeric", month: "short", year: "numeric" }
                          : { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                          {t.taskDetails?.length || 0} task(s)
                        </Typography>
                        {/* On phones the hours pill lives here, under the date, so it
                            never competes with the date text for row width. */}
                        {isMobile && (
                          <Chip size="small" label={`${rowTotal.toFixed(1)} hrs`} sx={{ height: 20, bgcolor: rowTotal >= 8 ? "#EDFAF2" : "#FEFCE8", color: rowTotal >= 8 ? "#39C645" : "#CA8A04", fontWeight: 700 }} />
                        )}
                      </Stack>
                    </Box>
                    {!isMobile && (
                      <Chip label={`${rowTotal.toFixed(1)} hrs`} sx={{ flexShrink: 0, bgcolor: rowTotal >= 8 ? "#EDFAF2" : "#FEFCE8", color: rowTotal >= 8 ? "#39C645" : "#CA8A04", fontWeight: 700 }} />
                    )}
                    {canEditTimesheet(t.status) && (
                      <Tooltip title="Edit timesheet">
                        <IconButton
                          size="small"
                          color="primary"
                          sx={{ flexShrink: 0 }}
                          onClick={(e) => { e.stopPropagation(); setEditTarget(t); }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton size="small" sx={{ flexShrink: 0 }}>{isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                  </Stack>
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Box sx={{ borderTop: "1px solid", borderColor: "divider", bgcolor: "background.default" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Task</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Project</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Hours</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(t.taskDetails || []).map((task) => (
                            <TableRow key={task._id} hover>
                              <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{task.taskName}</TableCell>
                              <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{task.projectName || task.project || "—"}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: "primary.main" }}>{parseFloat(task.duration || 0).toFixed(1)}</TableCell>
                              <TableCell align="right">
                                {canEditTimesheet(t.status) && (
                                  <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                                    <Tooltip title="Edit task">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={(e) => { e.stopPropagation(); setEditTarget(t); }}
                                      >
                                        <EditOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete task">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={(e) => { e.stopPropagation(); deleteTask(t._id, task._id); }}
                                      >
                                        <DeleteOutlineOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Stack>
        )}

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
          </Box>
        )}
      </SectionCard>

      <EditTimesheetDialog
        open={!!editTarget}
        timesheet={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={load}
      />
    </Box>
  );
}
